import {
  type UseMutationOptions,
  type UseMutationReturnType,
  type UseQueryOptions,
  type UseQueryReturnType,
  useMutation,
  useQuery,
} from '@tanstack/vue-query'
import { clearAuth } from '@/composables/useAuth.ts'
import { getErrorMessage } from '@/utils/errorUtils'
import { useToast } from './useToast'

type MutationOptionsWithOnError<TData, TError, TVariables, TContext> = UseMutationOptions<
  TData,
  TError,
  TVariables,
  TContext
> & {
  onError?: (error: TError, variables: TVariables, context: TContext | undefined) => void
}

// Define the toast type
type ToastReturnType = ReturnType<typeof useToast>

// Type guard for HTTP error
interface HttpError {
  status: number
  message?: string
  errors?: unknown[] | unknown
}

const getErrorMessageWithFallback = (err: unknown, fallbackMessage: string) => {
  const backendMessage = getErrorMessage(err)

  return backendMessage && backendMessage !== 'خطای ناشناخته رخ داده است' ? backendMessage : fallbackMessage
}

function isHttpError(err: unknown): err is HttpError {
  return err !== null && typeof err === 'object' && 'status' in err && typeof err.status === 'number'
}

const handleError = (err: unknown, toast: ToastReturnType) => {
  // Check if it's an HTTP error with status property
  if (isHttpError(err)) {
    // Handle 401 Unauthorized specifically
    if (err.status === 401) {
      toast.error('نشست شما منقضی شده است. لطفاً مجدداً وارد شوید.', 'خطا')
      clearAuth()
      document.location.href = '/auth/login'
      return
    }

    // Handle other status codes
    switch (err.status) {
      // TODO: we can display all errors message here;
      case 400:
        toast.error(getErrorMessageWithFallback(err, 'درخواست نامعتبر'), 'خطا')
        break

      case 403:
        toast.error(getErrorMessageWithFallback(err, 'شما دسترسی به این بخش ندارید'), 'خطا')
        break

      case 404:
        toast.error(getErrorMessageWithFallback(err, 'منبع مورد نظر یافت نشد'), 'خطا')
        break

      case 413:
        toast.error(getErrorMessageWithFallback(err, 'حجم درخواست بیش از حد مجاز است'), 'خطا')
        break

      case 422:
        toast.error(getErrorMessageWithFallback(err, 'اطلاعات وارد شده معتبر نیست'), 'خطا')
        break

      case 500:
        toast.error(getErrorMessageWithFallback(err, 'خطای داخلی سرور'), 'خطا')
        break
      default:
        toast.error(getErrorMessage(err), 'خطا')
    }
  } else {
    // Fallback for non-HTTP errors
    toast.error(getErrorMessage(err), 'خطا')
  }
}

/**
 * 🔹 useQueryApi — wrapper around useQuery
 */

export function useQueryApi<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends readonly unknown[] = readonly unknown[],
>(options: UseQueryOptions<TQueryFnData, TError, TData, TQueryFnData, TQueryKey>): UseQueryReturnType<TData, TError> {
  const toast = useToast()

  return useQuery({
    ...options,
    throwOnError: (error: TError) => {
      if (isHttpError(error)) {
        handleError(error, toast)
      }
      return true // or false, depending on your needs
    },
  })
}

/**
 * 🔹 useMutationApi — wrapper around useMutation
 */

export function useMutationApi<TData = unknown, TVariables = void, TError = unknown, TContext = unknown>(
  options: MutationOptionsWithOnError<TData, TError, TVariables, TContext>
): UseMutationReturnType<TData, TError, TVariables, TContext> {
  const toast = useToast()
  const { onError, ...mutationOptions } = options

  // Create a properly typed onError handler
  return useMutation({
    ...mutationOptions,
    onError: (error: TError, variables, context) => {
      // Handle the error with our toast
      handleError(error, toast)
      onError?.(error, variables, context)
    },
  })
}
